import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import { FiPlus, FiEdit2, FiTrash2, FiList } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import {
  partMainId,
  partOutputMain,
  partBaseLabel,
} from "./partMachinesUtils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const tableColumns = [
  {
    name: "Makine Kodu",
    dataType: "text",
    id: 1,
    customSorter: (a, b) =>
      String(a?.machine?.code || "").localeCompare(
        String(b?.machine?.code || ""),
      ),
  },
  {
    name: "Makine Adı",
    dataType: "text",
    id: 2,
    customSorter: (a, b) =>
      String(a?.machine?.name || "").localeCompare(
        String(b?.machine?.name || ""),
      ),
  },
  { name: "Üretilen Parçalar", id: 3, sortable: false },
  { name: "Kayıt", id: 4, sortable: false },
  { name: "İşlemler", id: 5, sortable: false },
];

const PartMachines = () => {
  useTitle("Rovion MES - Parça/Makine");
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/part-machines",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("part-machines-list", tableColumns);

  const [records, setRecords] = useState([]);
  const [mainParts, setMainParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    machineId: null,
    machineLabel: "",
  });

  const dismissToast = useCallback(
    (id) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/part-machines`,
        authHeaders(),
      );
      setRecords(data.partMachines || []);
    } catch {
      addToast("Kayıtlar yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast]);

  const fetchMainParts = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/part-machines/main-parts`,
        authHeaders(),
      );
      setMainParts(data.mainParts || []);
    } catch {}
  }, [authHeaders]);

  useEffect(() => {
    fetchRecords();
    fetchMainParts();
  }, [fetchRecords, fetchMainParts]);

  const machineGroups = useMemo(() => {
    const map = new Map();
    records.forEach((r) => {
      const mid = String(r.machine?._id || r.machine || "");
      if (!mid) return;
      if (!map.has(mid)) {
        map.set(mid, { machine: r.machine, partRecords: [] });
      }
      map.get(mid).partRecords.push(r);
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.machine?.code || "").localeCompare(
        String(b.machine?.code || ""),
      ),
    );
  }, [records]);

  const openDeleteMachineConfirm = (group) => {
    const mid = String(group.machine?._id || group.machine || "");
    const label = [group.machine?.code, group.machine?.name]
      .filter(Boolean)
      .join(" — ");
    setConfirmModal({ isOpen: true, machineId: mid, machineLabel: label });
  };

  const confirmDeleteMachine = async () => {
    const mid = confirmModal.machineId;
    if (!mid) return;
    try {
      await axios.put(
        `${API_URL}/api/part-machines/by-machine/${mid}`,
        { parts: [] },
        authHeaders(),
      );
      addToast("Makine ataması silindi", "success");
      await fetchRecords();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, machineId: null, machineLabel: "" });
    }
  };

  return (
    <AppPage
      title="Parça / Makine"
      actions={
        canWrite && (
          <Button
            appearance="primary-animated"
            onClick={() => navigate("/part-machines/assign")}
            icon={<FiPlus />}
            disabled={mainParts.length === 0}
          >
            Makine Ataması
          </Button>
        )
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "error" ? "danger" : t.type || "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal({ isOpen: false, machineId: null, machineLabel: "" })
        }
        width="small"
      >
        <ModalHeader>Makine Atamasını Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            <strong>{confirmModal.machineLabel || "Makine"}</strong> için tüm
            parça atamaları silinsin mi?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button appearance="danger" onClick={confirmDeleteMachine}>
              Sil
            </Button>
            <Button
              appearance="subtle"
              onClick={() =>
                setConfirmModal({
                  isOpen: false,
                  machineId: null,
                  machineLabel: "",
                })
              }
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <CardContainer>
        <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          tableId="part-machines-list"
          loading={loading}
          search={true}
          viewOptions={
            <ListViewOptionsPanel
              columns={tableColumns}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              showApply={false}
              showReset={false}
            />
          }
        >
          {machineGroups.map((group) => {
            const mid = String(group.machine?._id || group.machine);
            const activeCount = group.partRecords.filter(
              (r) => r.isActive !== false,
            ).length;
            return (
              <Tr key={mid} data={group}>
                {visibleColumns.includes(1) && (
                <Td>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      background: "var(--m-badge-default-subtle-bg, #f1f5f9)",
                      padding: "2px 6px",
                      borderRadius: 4,
                    }}
                  >
                    {group.machine?.code || "—"}
                  </span>
                </Td>
                )}
                {visibleColumns.includes(2) && (
                <Td>
                  <strong style={{ fontSize: "0.875rem" }}>
                    {group.machine?.name || "—"}
                  </strong>
                </Td>
                )}
                {visibleColumns.includes(3) && (
                <Td>
                  <div className="d-flex flex-wrap gap-1">
                    {(() => {
                      const seen = new Set();
                      return group.partRecords
                        .filter((r) => {
                          const key = partMainId(r.part);
                          if (!key || seen.has(key)) return false;
                          seen.add(key);
                          return true;
                        })
                        .map((r) => (
                          <span
                            key={partMainId(r.part)}
                            style={{
                              fontSize: "0.78rem",
                              padding: "2px 8px",
                              borderRadius: 4,
                              background:
                                r.isActive !== false ? "#e0f2fe" : "#f1f5f9",
                              color:
                                r.isActive !== false ? "#0369a1" : "#64748b",
                            }}
                            title={partBaseLabel(
                              partOutputMain(r.part) || r.part,
                            )}
                          >
                            {(partOutputMain(r.part) || r.part)?.code || "—"}
                          </span>
                        ));
                    })()}
                  </div>
                </Td>
                )}
                {visibleColumns.includes(4) && (
                <Td>
                  <Lozenge
                    appearance={
                      activeCount > 0 ? "success-subtle" : "default-subtle"
                    }
                    isBold
                  >
                    {(() => {
                      const bases = new Set(
                        group.partRecords.map((r) => partMainId(r.part)),
                      );
                      return `${bases.size} parça · ${group.partRecords.length} varyant`;
                    })()}
                    {activeCount < group.partRecords.length
                      ? ` (${activeCount} aktif)`
                      : ""}
                  </Lozenge>
                </Td>
                )}
                {visibleColumns.includes(5) && (
                <Td>
                  {canWrite && (
                    <div className="d-flex gap-2">
                      <IconButton
                        icon={<FiList />}
                        label="Detay"
                        appearance="subtle"
                        shape="circle"
                        onClick={() => navigate(`/part-machines/view/${mid}`)}
                      />
                      <IconButton
                        icon={<FiEdit2 />}
                        label="Düzenle"
                        appearance="subtle"
                        shape="circle"
                        onClick={() =>
                          navigate(`/part-machines/assign/${mid}`)
                        }
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        label="Sil"
                        appearance="subtle"
                        shape="circle"
                        onClick={() => openDeleteMachineConfirm(group)}
                      />
                    </div>
                  )}
                </Td>
                )}
              </Tr>
            );
          })}
        </Table>
      </CardContainer>
    </AppPage>
  );
};

export default withAuth(PartMachines);
